import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {AppointmentType, CalendarAppointment, CalendarAppointmentPatient, Patient, PatientDisplayFormat, PatientNamePipe, QueryPatient} from '@xmedical/core';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {DatePipe} from '@angular/common';
import {XmedicalUiModule} from '@xmedical/ui';

export interface DayTimeSlot {
  timeslot: Date;
  appointment: CalendarAppointment;
}

@Component({
  selector: 'app-day-view',
  templateUrl: './day-view.component.html',
  styleUrls: ['./day-view.component.scss'],
  imports: [
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    DatePipe,
    PatientNamePipe,
    XmedicalUiModule,
  ]
})
export class DayViewComponent implements OnChanges {
  @Input() visibleAppointmentTypes: number[];
  @Input() currentDate: Date;
  @Input() intervals: Date[];
  @Input() practiceLocationId: number | null = null;
  @Input() practicePractitionerId: number | null = null;
  @Input() appointments: CalendarAppointment[] = [];
  @Input() appointmentTypes: AppointmentType[] = [];
  @Input() patientDisplayFormat: PatientDisplayFormat | null = null;

  @Output() rowSelectionChanged = new EventEmitter<DayTimeSlot>();
  @Output() appointmentSelectionChanged = new EventEmitter<CalendarAppointment[]>();
  @Output() deleteRequested = new EventEmitter<CalendarAppointment>();
  @Output() editRequested = new EventEmitter<CalendarAppointment>();
  @Output() addRequested = new EventEmitter<{ timeslot: Date }>();
  @Output() viewPatientRequested = new EventEmitter<Patient | QueryPatient | CalendarAppointmentPatient>();
  @Output() createPatientRequested = new EventEmitter<CalendarAppointment>();
  @Output() sendMessageRequested = new EventEmitter<Patient | QueryPatient | CalendarAppointmentPatient>();
  @Output() sendEmailRequested = new EventEmitter<Patient | QueryPatient | CalendarAppointmentPatient>();
  @Output() reloadRequested = new EventEmitter<void>();

  selectedAppointments: CalendarAppointment[] = [];
  selectedRow: DayTimeSlot = null;
  displayedColumns: string[] = ['time', 'select', 'patientStatus', 'profileId', 'name', 'cell', 'identityNumber', 'reason', 'appointmentStatus', 'actions'];
  dataSource = new MatTableDataSource<DayTimeSlot>([]);

  ngOnChanges(changes: SimpleChanges) {
    this.rebuildDataSource();
  }

  get filteredAppointments(): CalendarAppointment[] {
    if (!this.visibleAppointmentTypes || this.visibleAppointmentTypes.length === 0) {
      return this.appointments;
    }
    return this.appointments.filter(a => this.visibleAppointmentTypes.includes(a.appointmentType.id));
  }

  get appointmentsSelected(): boolean {
    return this.selectedAppointments.length > 0;
  }

  selectRow(row: DayTimeSlot) {
    if (row.appointment) { return; }
    if (this.selectedRow === row) {
      this.selectedRow = null;
    } else {
      this.selectedRow = row;
    }
    this.rowSelectionChanged.emit(this.selectedRow);
  }

  removeAppointment(appointment: CalendarAppointment) {
    this.deleteRequested.emit(appointment);
  }

  editAppointment(appointment: CalendarAppointment) {
    this.editRequested.emit(appointment);
  }

  addNewAppointment() {
    this.addRequested.emit({timeslot: this.selectedRow.timeslot});
  }

  onAppointmentSelectionChanged(checked: boolean, appointment: CalendarAppointment) {
    if (checked) {
      this.selectedAppointments.push(appointment);
    } else {
      const index = this.selectedAppointments.indexOf(appointment);
      if (index > -1) {
        this.selectedAppointments.splice(index, 1);
      }
    }
    this.appointmentSelectionChanged.emit(this.selectedAppointments);
  }

  sendSingleMessage(appointment: CalendarAppointment) {
    this.sendMessageRequested.emit(appointment.patient);
  }

  sendSingleEmail(appointment: CalendarAppointment) {
    this.sendEmailRequested.emit(appointment.patient);
  }

  view(appointment: CalendarAppointment): void {
    this.viewPatientRequested.emit(appointment.patient);
  }

  createPatient(appointment: CalendarAppointment): void {
    this.createPatientRequested.emit(appointment);
  }

  private rebuildDataSource() {
    if (!this.currentDate || !this.intervals?.length) {
      return;
    }
    const timeslotAppointments: DayTimeSlot[] = [];
    this.intervals.forEach((timeSlot, index) => {
      const appointmentsForTimeSlot = this.getAppointmentsAtTimeSlot(timeSlot, this.intervals[index + 1]);
      appointmentsForTimeSlot.forEach(appointment => {
        timeslotAppointments.push({timeslot: timeSlot, appointment: appointment} as DayTimeSlot);
      });
      timeslotAppointments.push({timeslot: timeSlot, appointment: null} as DayTimeSlot);
    });
    const sortedAppointments = this.sortAppointments(timeslotAppointments);
    this.dataSource = new MatTableDataSource<DayTimeSlot>(sortedAppointments);
  }

  private getAppointmentsAtTimeSlot(time: Date, next: Date | undefined): CalendarAppointment[] {
    if (!time || !next) return [];
    return this.filteredAppointments.filter(a => {
      if (!a.start) return false;
      const startTime = new Date(a.start).getTime();
      return startTime >= time.getTime() && startTime < next.getTime();
    });
  }

  private sortAppointments(appointments: DayTimeSlot[]): DayTimeSlot[] {
    return appointments.sort((a, b) => {
      if (a.timeslot === b.timeslot) {
        return a.appointment?.title?.localeCompare(b.appointment?.title);
      }
      return a.timeslot > b.timeslot ? 1 : -1;
    });
  }
}
